import { HeaderCopy } from 'components/city-blocks/header-copy';
import { MaxWidthContainer } from 'components/city-blocks/max-width-container';
import { MetrosListSection } from 'components/city-blocks/metros-list-section';
import { HeroBlock } from 'components/hero-block/hero-block';
import Layout from 'components/layout';
import { generateBreadcrumbSchema, generateLocationListSchema, SchemaMarkup, SEOHead } from 'components/seo';
import Link from 'next/link';
import React from 'react';
import { fetchMetrosData } from 'utils/api-utils';
import { metroRootBreadcrumbs } from 'utils/breadcrumb-util';
import { getFrontEndUrl } from 'utils/environment-util';
import { RegionInfoFragment } from 'utils/gqlReactTypings.generated.d';
import { memoizedRegions } from 'utils/memoized-data-structure';

interface IProps {
  regions: RegionInfoFragment[];
}

const METRO_HOME_META_DESC =
  'Shef delivers authentic homemade food to major metropolitan areas across the US. Find local chefs and authentic cuisines in your metro area!';
const HEADER_COPY_TITLE = 'Find a Local Delivery or Pickup Option Near You For Delicious Homemade Food!';

const invokeHeaderCopyContent = ({ numMetros }: { numMetros: number }): React.ReactElement => (
  <span>
    Shef delivers traditional homemade food to over {numMetros} metropolitan areas throughout the US and has food pickup
    in many more! With thousands of local Shefs & dishes to choose from, you're sure to find the perfect homemade dish
    for breakfast, lunch, or dinner! Browse by metro, city, or{' '}
    <Link
      href='https://help.shef.com/en/'
      className='text-neutral-500 underline underline-offset-2 hover:text-neutral-700'>
      contact us
    </Link>{' '}
    to see when we might be near you!
  </span>
);

const MetrosWeServe: React.FC<IProps> = ({ regions }) => {
  const title = 'See All Metropolitan Areas Where Our Shefs Cook Homemade Food!';
  const heroText = 'Metro Areas Where Shef Delivers';
  const siteUrl = getFrontEndUrl();
  const pageUrl = `${siteUrl}/metros-we-serve`;

  const schemas = [
    generateBreadcrumbSchema([
      { name: 'Home', url: siteUrl },
      { name: 'Metros We Serve', url: pageUrl },
    ]),
    generateLocationListSchema(regions, 'metro', siteUrl),
  ];

  return (
    <Layout>
      <SEOHead
        title={title}
        description={METRO_HOME_META_DESC}
        url={pageUrl}
        keywords={[
          'metro areas where shef delivers',
          'metropolitan food delivery',
          'major city food delivery',
          'metro area chefs',
          'urban food delivery service',
        ]}
        image={`${siteUrl}/images/CDN/food_bg.png`}
        canonical={pageUrl}
      />
      <SchemaMarkup schemas={schemas} id='metros-schema' />
      <HeroBlock className='mb-10' heroText={heroText} crumbs={metroRootBreadcrumbs} />
      <MaxWidthContainer className='px-4 md:px-0'>
        <HeaderCopy
          className='mb-12'
          title={HEADER_COPY_TITLE}
          content={invokeHeaderCopyContent({ numMetros: regions.length })}
        />
      </MaxWidthContainer>

      <MaxWidthContainer className='px-4 md:px-0'>
        <MetrosListSection title='All metro areas Shef serves' regions={regions} />
      </MaxWidthContainer>
    </Layout>
  );
};

export async function getStaticProps() {
  const metrosData = await fetchMetrosData();

  if (!metrosData?.regions) {
    return {
      props: { regions: await memoizedRegions() },
    };
  }

  return {
    props: { regions: metrosData.regions },
  };
}

export default MetrosWeServe;
